/**
 * useSearch.js
 * Builds a flat search index from all searchable layer markers.
 * Index is rebuilt only when layers change — never on every keystroke.
 * Search itself is synchronous O(n) scan, fast enough for thousands of entries.
 */

/**
 * @returns {{ buildIndex, search }}
 */
export function createSearchManager() {
  /**
   * Flat array of indexable entries.
   * Shape: { id, layerId, searchText, lat, lng, leafletMarkerRef }
   * leafletMarkerRef is injected later by the layer manager watcher.
   */
  let index = [];

  /**
   * Rebuild the index from the current layers array.
   * Call this whenever layers.markers changes.
   * @param {Array} layers - layer config objects from the page
   */
  function buildIndex(layers) {
    index = [];
    layers.forEach(layer => {
      if (!layer.searchable) return;
      layer.markers.forEach(marker => {
        const fields = Array.isArray(marker.searchFields) ? marker.searchFields : [];
        index.push({
          id:         marker.id,
          layerId:    marker.layer,
          searchText: fields.join(' ').toLowerCase(),
          lat:        marker.lat,
          lng:        marker.lng,
          data:       marker.data,
        });
      });
    });
  }

  /**
   * Search the index for a query string.
   * Returns the first matching entry, or null.
   * @param {string} query
   * @returns {{ id, layerId, lat, lng, data } | null}
   */
  function search(query) {
    if (!query || !query.trim()) return null;
    const q = query.trim().toLowerCase();
    return index.find(entry => entry.searchText.includes(q)) || null;
  }

  /**
   * Return all matches (for future multi-result UI).
   * @param {string} query
   * @returns {Array}
   */
  function searchAll(query) {
    if (!query || !query.trim()) return [];
    const q = query.trim().toLowerCase();
    return index.filter(entry => entry.searchText.includes(q));
  }

  return { buildIndex, search, searchAll, getIndexSize: () => index.length };
}
